import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'success' | 'error';
  size?: 'sm' | 'md' | 'lg';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-medium rounded-md transition-all duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'active:scale-[0.98]',
          // Variants
          variant === 'primary' && [
            'bg-primary-500 text-white',
            'hover:bg-primary-600',
            'focus-visible:ring-primary-500',
          ],
          variant === 'secondary' && [
            'bg-secondary-500 text-white',
            'hover:bg-secondary-600',
            'focus-visible:ring-secondary-500',
          ],
          variant === 'ghost' && [
            'bg-transparent text-stone-700 dark:text-stone-300',
            'hover:bg-stone-100 dark:hover:bg-stone-800',
            'focus-visible:ring-stone-500',
          ],
          variant === 'success' && [
            'bg-lime-700 text-white',
            'hover:bg-lime-800',
            'focus-visible:ring-lime-600',
          ],
          variant === 'error' && [
            'bg-red-700 text-white',
            'hover:bg-red-800',
            'focus-visible:ring-red-600',
          ],
          // Sizes
          size === 'sm' && 'px-3 py-1.5 text-sm',
          size === 'md' && 'px-4 py-2 text-base',
          size === 'lg' && 'px-5 py-2.5 text-lg',
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
